import React from 'react'

import Link from 'next/link'
import {
  HiOutlineBolt,
  HiOutlineMagnifyingGlass,
  HiOutlineUserGroup,
} from 'react-icons/hi2'
import { LuExternalLink, LuWand2 } from 'react-icons/lu'

import Button from '@/components/common/Button'
import { Skeleton } from '@/components/common/Skeleton'
import { Surface } from '@/components/home/ui/Surface'

import { DEMO_URL } from '@/constants/general'

import { useActiveOrg } from '@/hooks/organization/useActiveOrg'

const features = [
  {
    icon: HiOutlineMagnifyingGlass,
    title: 'AI Opportunity Discovery',
    description:
      'Find 3x more relevant opportunities with advanced AI search across 300,000+ sources.',
  },
  {
    icon: LuWand2,
    title: 'Proposal Assistant',
    description:
      'Generate winning proposals in hours, not weeks, with automated compliance checking.',
  },
  {
    icon: HiOutlineUserGroup,
    title: 'Team Collaboration',
    description:
      'Track pursuits, assign tasks, and collaborate seamlessly in one unified workspace.',
  },
  {
    icon: HiOutlineBolt,
    title: 'All-in-One Workflow',
    description:
      'Streamline your entire contracting process from discovery to submission.',
  },
]

export const UpsellFallback: React.FC = () => {
  const { activeOrg: organization, isLoading } = useActiveOrg()

  return (
    <div className="w-full pb-6">
      <Surface>
        <Surface.Content className="p-0">
          <div className="flex flex-col lg:flex-row">
            {/* Left side: Message and CTA */}
            <div className="flex flex-1 flex-col justify-center p-8 text-left lg:p-12">
              <div className="mb-6">
                <h2 className="mb-4 text-2xl font-bold tracking-tight text-primary-900 dark:text-primary-100 lg:text-3xl">
                  Imagine this power for{' '}
                  <span className="text-accent-500">your team</span>
                </h2>

                {isLoading ? (
                  <Skeleton className="h-5 w-3/4" />
                ) : (
                  <p className="max-w-xl text-base leading-relaxed text-primary-600 dark:text-primary-400">
                    {`You're currently viewing the ${
                      organization?.name ?? '...'
                    } workspace as a guest. Schedule a demo to learn how Sweetspot can support your team.`}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Link href={DEMO_URL} target="_blank" rel="noopener noreferrer">
                  <Button variant="primary" className="px-6">
                    Schedule Your Demo
                  </Button>
                </Link>
                <Link
                  href="https://sweetspot.so"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="gap-2 px-6">
                    Learn More
                    <LuExternalLink className="h-3.5 w-3.5 opacity-60" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right side: Features grid as a visual accent */}
            <div className="relative border-t border-primary-100 bg-accent-50/30 p-8 dark:border-primary-800 dark:bg-accent-950/10 lg:w-2/5 lg:border-l lg:border-t-0">
              <div className="relative grid gap-8 sm:grid-cols-2 lg:grid-cols-1">
                {features.map((feature, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-primary-100 dark:bg-primary-800 dark:ring-primary-800">
                      <feature.icon className="h-4.5 w-4.5 text-accent-500" />
                    </div>
                    <div className="flex flex-col justify-center">
                      <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100">
                        {feature.title}
                      </h3>
                      <p className="mt-0.5 text-xs leading-normal text-primary-500 dark:text-primary-400">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Surface.Content>
      </Surface>
    </div>
  )
}
